import React, { useState } from 'react';
import { ListFilter } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { cn } from 'shadcn/lib/utils';

export const ENABLED_FILTER_ALL = 'all';
export const ENABLED_FILTER_ENABLED = 'enabled';
export const ENABLED_FILTER_DISABLED = 'disabled';

const ENABLED_FILTER_OPTIONS = [
    { label: 'All', value: ENABLED_FILTER_ALL },
    { label: 'Enabled', value: ENABLED_FILTER_ENABLED },
    { label: 'Disabled', value: ENABLED_FILTER_DISABLED },
];

/**
 * Popover enabled-status filter with toggle pills for SubstitutionRulesTable.
 *
 * @param {Object} props
 * @param {import('@tanstack/react-table').Column<Record<string, unknown>, unknown>} props.column
 * @param {string} [props.triggerAriaLabel]
 */
export function SubstitutionRulesColumnEnabledFilter({ column, triggerAriaLabel = 'Filter enabled status column' }) {
    const filterValue = column.getFilterValue();
    const selectedValue =
        filterValue === ENABLED_FILTER_ENABLED || filterValue === ENABLED_FILTER_DISABLED
            ? filterValue
            : ENABLED_FILTER_ALL;
    const hasActiveFilter = selectedValue !== ENABLED_FILTER_ALL;
    const [open, setOpen] = useState(false);

    const handleSelect = (value) => {
        column.setFilterValue(value === ENABLED_FILTER_ALL ? undefined : value);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    className={cn(
                        'relative size-7 shrink-0 text-muted-foreground hover:text-foreground',
                        hasActiveFilter &&
                            'bg-primary/15 text-primary ring-2 ring-primary/35 hover:bg-primary/20 hover:text-primary',
                    )}
                    aria-label={triggerAriaLabel}
                    aria-pressed={hasActiveFilter}
                    data-filter-active={hasActiveFilter ? 'true' : 'false'}
                    onClick={(event) => event.stopPropagation()}
                >
                    <ListFilter className="size-3.5" aria-hidden />
                    {hasActiveFilter ? (
                        <span
                            className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-primary ring-1 ring-background"
                            aria-hidden
                        />
                    ) : null}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start" onClick={(event) => event.stopPropagation()}>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Enabled status filter">
                    {ENABLED_FILTER_OPTIONS.map(({ label, value }) => {
                        const isSelected = selectedValue === value;
                        return (
                            <Button
                                key={value}
                                type="button"
                                variant={isSelected ? 'default' : 'outline'}
                                size="sm"
                                className={cn('h-8 rounded-full px-3 text-xs', !isSelected && 'text-muted-foreground')}
                                aria-pressed={isSelected}
                                onClick={() => handleSelect(value)}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}

SubstitutionRulesColumnEnabledFilter.displayName = 'SubstitutionRulesColumnEnabledFilter';
